import React from 'react';
import styled, { keyframes } from 'styled-components';
import { Modal, Text } from 'components';
import { Button } from '../inputs';
import { getColor } from 'utils';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: scale(0.96);
  }
  to {
    opacity: 1;
    transform: scale(1);
  }
`;

const WelcomeContent = styled.div`
  max-width: 320px;
  width: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 12px;
  padding: 12px 16px;
  box-sizing: border-box;
  animation: ${fadeIn} 0.3s ease-out;
`;

const AvatarContainer = styled.div`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${() => getColor('brand')() || '#404bee'} 0%, ${() => getColor('brandDark')() || '#1932a0'} 100%);
  display: flex;
  align-items: center;
  justify-content: center;
  font-family: 'GilroyRegular';
  font-size: 22px;
  font-weight: 600;
  color: white;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
`;

const UserName = styled.h2`
  font-family: 'GilroyRegular';
  font-size: 18px;
  font-weight: 600;
  margin: 0;
  color: #111;
  text-align: center;
  line-height: 1.3;
`;

const WelcomeMessage = styled.p`
  font-family: 'GilroyRegular';
  font-size: 14px;
  font-weight: 500;
  margin: 0;
  color: #555;
  line-height: 1.4;
  text-align: center;
`;

const AcceptButton = styled(Button)`
  margin-top: 4px;
  min-width: 120px;
  padding: 10px 20px;
  background-color: ${() => getColor('brand')() || '#404bee'} !important;
  color: white !important;
  border: none;
  font-family: 'GilroyRegular';
  font-weight: 600;
  transition: background-color 0.2s ease, transform 0.1s ease, box-shadow 0.2s ease !important;

  &:hover {
    background-color: ${() => getColor('brand')() || '#404bee'} !important;
    opacity: 0.82;
  }

  &:active {
    background-color: ${() => getColor('brand')() || '#404bee'} !important;
    opacity: 0.65;
    transform: scale(0.98);
  }
`;

interface WelcomePopupProps {
  isOpen: boolean;
  onClose: () => void;
  user: {
    emailAddress?: string;
    name?: string;
    displayName?: string;
    firstName?: string;
    lastName?: string;
    profileImageUrl?: string;
    profile?: { companyName?: string; name?: string };
  };
  appType?: string;
}

const getDisplayName = (user: WelcomePopupProps['user']): string => {
  if (user?.name) return user.name;
  if (user?.displayName) return user.displayName;
  if (user?.firstName || user?.lastName) {
    return [user.firstName, user.lastName].filter(Boolean).join(' ').trim();
  }
  if (user?.emailAddress) return user.emailAddress.split('@')[0];
  return 'User';
};

const getInitials = (name: string): string => {
  const parts = name.trim().split(/\s+/);
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase();
  }
  return name.slice(0, 2).toUpperCase();
};

export const WelcomePopup: React.FC<WelcomePopupProps> = ({
  isOpen,
  onClose,
  user,
  appType = 'Insights',
}) => {
  const displayName = getDisplayName(user);
  const initials = getInitials(displayName);

  return (
    <Modal
      isOpen={isOpen}
      requestClose={onClose}
      position=""
      size="small"
    >
      <WelcomeContent>
        {user?.profileImageUrl ? (
          <img
            src={user.profileImageUrl}
            alt={displayName}
            style={{
              width: 56,
              height: 56,
              borderRadius: '50%',
              objectFit: 'cover',
              boxShadow: '0 2px 8px rgba(0, 0, 0, 0.15)',
            }}
          />
        ) : (
          <AvatarContainer>{initials}</AvatarContainer>
        )}
        <UserName>Welcome back, {displayName}!</UserName>
        <WelcomeMessage>
          {`You can now manage Edge email subscriptions in Pistil Insights, including the new Inventory Edge. Access them under "Menu" in the top right corner of the app.`}
        </WelcomeMessage>
        <AcceptButton onClick={onClose}>
          <Text color="white" textStyle="button">Got it</Text>
        </AcceptButton>
      </WelcomeContent>
    </Modal>
  );
};

export default WelcomePopup;
